// app/javascript/components/shared/ThemeMenu.tsx
import React from 'react'
import { Sun, Moon, Check } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { useTheme } from './ThemeProvider'
import { THEME_FAMILIES } from '@/lib/themes'

interface ThemeMenuProps {
  align?: 'start' | 'center' | 'end'
  className?: string
}

export function ThemeMenu({ align = 'end', className = '' }: ThemeMenuProps) {
  const { currentTheme, availableThemes, setTheme, mode, toggleMode } = useTheme()

  const uniqueFamilies = THEME_FAMILIES.map((family) => {
    const activeVariantName = mode === 'dark' ? family.darkThemeName : family.lightThemeName
    const activeVariant =
      availableThemes.find((t) => t.name === activeVariantName) ||
      availableThemes.find((t) => t.family === family.id)

    return {
      family,
      activeVariant,
      isCurrentlyActiveFamily: currentTheme.family === family.id,
    }
  })

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Appearance settings"
          title="Change Theme & Mode"
          className={`h-9 w-9 rounded-xl hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors ${className}`}
        >
          {mode === 'dark' ? (
            <Moon className="h-4.5 w-4.5 text-secondary animate-fade-in" />
          ) : (
            <Sun className="h-4.5 w-4.5 text-tertiary animate-fade-in" />
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align={align}
        sideOffset={6}
        className="w-56 p-2 rounded-xl bg-card/95 backdrop-blur-xl border-border/80 shadow-glass-lg"
      >
        <DropdownMenuLabel className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          Appearance
        </DropdownMenuLabel>

        {/* Mode Selector Segment */}
        <div className="p-1 my-1 bg-muted/50 rounded-lg border border-border/50 flex items-center gap-1">
          <button
            type="button"
            onClick={() => {
              if (mode !== 'light') toggleMode()
            }}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-semibold transition-all ${
              mode === 'light'
                ? 'bg-background text-foreground shadow-xs font-bold'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Sun className="h-3.5 w-3.5 text-tertiary" />
            <span>Light</span>
          </button>
          <button
            type="button"
            onClick={() => {
              if (mode !== 'dark') toggleMode()
            }}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-semibold transition-all ${
              mode === 'dark'
                ? 'bg-background text-foreground shadow-xs font-bold'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Moon className="h-3.5 w-3.5 text-secondary" />
            <span>Dark</span>
          </button>
        </div>

        <DropdownMenuSeparator className="my-1.5" />

        <DropdownMenuLabel className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Theme Palettes
        </DropdownMenuLabel>

        {/* Color Palettes List */}
        <div className="space-y-0.5">
          {uniqueFamilies.map(({ family, activeVariant, isCurrentlyActiveFamily }) => (
            <DropdownMenuItem
              key={family.id}
              onClick={() => {
                if (activeVariant) setTheme(activeVariant.name)
              }}
              className={`flex items-center justify-between cursor-pointer py-1.5 px-2 rounded-lg text-xs transition-colors ${
                isCurrentlyActiveFamily
                  ? 'bg-primary/15 text-primary font-bold'
                  : 'text-foreground hover:bg-muted/60'
              }`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="flex items-center -space-x-1 shrink-0">
                  <span
                    className="w-3 h-3 rounded-full border border-border shadow-xs"
                    style={{ backgroundColor: family.swatches[0] }}
                  />
                  <span
                    className="w-3 h-3 rounded-full border border-border shadow-xs"
                    style={{ backgroundColor: family.swatches[1] }}
                  />
                  <span
                    className="w-3 h-3 rounded-full border border-border shadow-xs"
                    style={{ backgroundColor: family.swatches[2] }}
                  />
                </div>
                <span className="truncate">{family.displayName}</span>
              </div>
              {isCurrentlyActiveFamily && <Check className="h-3.5 w-3.5 text-primary shrink-0 ml-1.5" />}
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
